import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiX } from 'react-icons/fi';
import { BsImages } from 'react-icons/bs';
import HEADER1 from '../assets/HEADER 1.jpg';
import HEADER2 from '../assets/HEADER 2.jpg';
import HEADER3 from '../assets/HEADER 3.jpg';
import ABOUT_NAVYA from '../assets/ABOUT NAVYA.jpg';
import LOTUS from '../assets/LOTUS.jpg';
import ORCHID from '../assets/ORCHID.jpg';
import JASMINE from '../assets/JASMINE.jpg';
import GLADIOLUS from '../assets/Gladiolus 1.JPG';

const Gallery = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [selectedImage, setSelectedImage] = useState(null);

  const images = [
    { url: HEADER1, title: "Exterior View", category: "Property" },
    { url: ABOUT_NAVYA, title: "About Navyaa", category: "Property" },
    { url: ORCHID, title: "Orchid Apartment", category: "Rooms" },
    { url: LOTUS, title: "Lotus Room", category: "Rooms" },
    { url: JASMINE, title: "Jasmine Apartment", category: "Rooms" },
    { url: GLADIOLUS, title: "Gladiolus Room", category: "Rooms" },
    { url: HEADER2, title: "Garden Area", category: "Outdoors" },
    { url: HEADER3, title: "Sunset View", category: "Views" },
  ];

  return (
    <section id="gallery" className="section-padding bg-gradient-to-b from-white to-primary-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <BsImages className="text-primary-500 text-5xl mx-auto" />
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Photo Gallery
          </h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Take a visual tour of our beautiful homestay and its surroundings
          </p>
        </motion.div>

        {/* Masonry Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.05, zIndex: 10 }}
              className={`relative overflow-hidden rounded-xl cursor-pointer shadow-lg hover:shadow-2xl transition-all group ${
                index % 7 === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
              onClick={() => setSelectedImage(image)}
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="font-bold text-lg">{image.title}</p>
                  <p className="text-sm text-primary-200">{image.category}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Video Section */}
        {/* Commented out until video is available
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-16 bg-white rounded-2xl p-8 shadow-xl"
        >
          <h3 className="text-3xl font-bold text-center mb-6">Experience Navyaa</h3>
          <div className="aspect-video bg-gray-200 rounded-xl overflow-hidden">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/dQw4w9WgXcQ"
              title="Navyaa Homestay Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </motion.div>
        */}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-primary-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <FiX size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              src={selectedImage.url}
              alt={selectedImage.title}
              className="max-w-full max-h-full rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-center">
              <p className="text-2xl font-bold">{selectedImage.title}</p>
              <p className="text-primary-300">{selectedImage.category}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
